import { customerRoutes } from 'noddi-util';

/**
 * The stationary proposal flow as an explicit ordered list of steps.
 *
 * Back navigation is the previous step in this list — never inferred from whether
 * a hold exists. Deriving it from store state is what produced the summary ⇄
 * calendar ping-pong: holding a time made the landing page redirect forward, so
 * every back edge into it bounced straight back down the flow.
 */
export type StationaryProposalStep = 'chooseTime' | 'calendar' | 'summary';

type FlowContext = {
  /**
   * Brand suggests three times on the landing page, so that page is the
   * choose-time step and the calendar is the detour off it. Other brands choose
   * their time in the calendar itself.
   */
  usesSuggestedTimes: boolean;
};

/**
 * Ordered steps for a flow. The login step is deliberately absent: it is a gate
 * in front of the summary, not a place to send someone back to — a signed-in
 * customer is bounced off it, which would make it a dead back target.
 */
export const getStationaryProposalSteps = ({ usesSuggestedTimes }: FlowContext): StationaryProposalStep[] =>
  usesSuggestedTimes ? ['chooseTime', 'summary'] : ['chooseTime', 'calendar', 'summary'];

export const getStationaryProposalStepPath = (step: StationaryProposalStep, slug: string): string => {
  switch (step) {
    case 'chooseTime':
      return customerRoutes.bookingProposalLandingPage.getPath({ slug });
    case 'calendar':
      return customerRoutes.stationaryProposalTimeWindow.getPath({ slug });
    case 'summary':
      return customerRoutes.stationaryProposalSummary.getPath({ slug });
  }
};

/** First step of the flow — where an invalid deep link is sent. */
export const getStationaryProposalFirstStepPath = (slug: string, context: FlowContext): string =>
  getStationaryProposalStepPath(getStationaryProposalSteps(context)[0]!, slug);

type BackPathArgs = FlowContext & {
  step: StationaryProposalStep | 'login';
  slug: string;
};

/**
 * Path the back button on `step` points at, or undefined for the flow entry.
 * The login gate stands in front of the summary, so it goes back where the
 * summary would. The calendar is a detour off the choose-time step when the
 * brand suggests times, so it returns there.
 */
export const getStationaryProposalBackPath = ({ step, slug, usesSuggestedTimes }: BackPathArgs): string | undefined => {
  const steps = getStationaryProposalSteps({ usesSuggestedTimes });

  if (step === 'login') {
    return getStationaryProposalBackPath({ step: 'summary', slug, usesSuggestedTimes });
  }

  // The calendar replaces the landing page as the choose-time step for brands
  // without suggestions, so it is in `steps`; for the others it is a detour.
  if (step === 'calendar' && usesSuggestedTimes) {
    return getStationaryProposalStepPath('chooseTime', slug);
  }

  const previous = steps[steps.indexOf(step) - 1];
  return previous ? getStationaryProposalStepPath(previous, slug) : undefined;
};

/** Where a customer goes once a time is held: the login gate while signed out, else the summary. */
export const getStationaryProposalNextPathAfterTimeChosen = ({
  slug,
  isLoggedIn
}: {
  slug: string;
  isLoggedIn: boolean;
}): string =>
  !isLoggedIn
    ? customerRoutes.stationaryProposalLogin.getPath({ slug })
    : getStationaryProposalStepPath('summary', slug);
